import React, { useEffect, useState, useCallback } from 'react';
import { Container, Table, Badge, Button, Modal, Form, Dropdown } from 'react-bootstrap';
import { forumApi } from '../../api/forum';
import HrnbCard from '../../components/controls/HrnbCard';
import HrnbAlert from '../../components/controls/HrnbAlert';
import HrnbLoader from '../../components/controls/HrnbLoader';
import AdminPagination from '../components/pagination/AdminPagination';
import './AdminForumPosts.css';

const AdminForumPosts = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);

    // Пагинация
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    // Загрузка постов
    const loadPosts = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const params = {
                page: currentPage - 1,
                size: pageSize
            };
            if (statusFilter !== 'ALL') {
                params.status = statusFilter;
            }
            const data = await forumApi.listPosts(params);
            console.log('[AdminForumPosts] Loaded posts:', data);

            let postsList = [];
            let total = 0;

            if (Array.isArray(data)) {
                postsList = data;
                total = data.length;
            } else if (data && Array.isArray(data.content)) {
                postsList = data.content;
                total = data.totalElements || data.total || data.content.length;
            }

            setPosts(postsList);
            setTotalItems(total);
        } catch (err) {
            setError(`Не удалось загрузить посты: ${err.message}`);
            console.error('[AdminForumPosts] Failed to load posts:', err);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, currentPage, pageSize]);

    useEffect(() => {
        loadPosts();
    }, [loadPosts]);

    // Фильтрация по поиску
    const filteredPosts = posts.filter(post =>
        post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.content?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Получение цвета бейджа статуса
    const getStatusBadgeVariant = (status) => {
        switch (status) {
            case 'PUBLISHED':
                return 'success';
            case 'HIDDEN':
                return 'warning';
            case 'DELETED':
                return 'danger';
            default:
                return 'secondary';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'PUBLISHED':
                return 'Опубликован';
            case 'HIDDEN':
                return 'Скрыт';
            case 'DELETED':
                return 'Удалён';
            default:
                return status;
        }
    };

    // Одобрить пост (снять с модерации)
    const handleApprove = async (post) => {
        if (!window.confirm(`Опубликовать пост "${post.title}"?`)) {
            return;
        }

        try {
            setActionLoading(true);
            await forumApi.updatePostStatus(post.id, 'PUBLISHED');
            await loadPosts();
        } catch (err) {
            setError(`Не удалось одобрить пост: ${err.message}`);
            console.error('[AdminForumPosts] Failed to approve post:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // Скрыть пост
    const handleHide = async (post) => {
        if (!window.confirm(`Скрыть пост "${post.title}"?`)) {
            return;
        }

        try {
            setActionLoading(true);
            await forumApi.updatePostStatus(post.id, 'HIDDEN');
            await loadPosts();
        } catch (err) {
            setError(`Не удалось скрыть пост: ${err.message}`);
            console.error('[AdminForumPosts] Failed to hide post:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // Удалить пост
    const handleDeletePost = async (post) => {
        if (!window.confirm(`Удалить пост "${post.title}"?\n\nЭто действие необратимо!`)) {
            return;
        }

        try {
            setActionLoading(true);
            await forumApi.updatePostStatus(post.id, 'DELETED');
            await loadPosts();
        } catch (err) {
            setError(`Не удалось удалить пост: ${err.message}`);
            console.error('[AdminForumPosts] Failed to delete post:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // Удалить пост
    const handleDelete = async (post) => {
        if (!window.confirm(`Удалить пост "${post.title}"?\n\nЭто действие необратимо!`)) {
            return;
        }

        try {
            setActionLoading(true);
            await forumApi.deletePost(post.id);
            await loadPosts();
        } catch (err) {
            setError(`Не удалось удалить пост: ${err.message}`);
            console.error('[AdminForumPosts] Failed to delete post:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // Открытие модального окна с деталями поста
    const handleViewDetails = (post) => {
        setSelectedPost(post);
        setShowDetailModal(true);
    };

    if (loading) {
        return (
            <Container fluid className="admin-loading">
                <HrnbLoader />
            </Container>
        );
    }

    return (
        <Container fluid>
            <h1 className="admin-page-title">Модерация постов</h1>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <div className="admin-pagination-header">
                <Form.Select
                    className="admin-page-size-selector"
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                    }}
                >
                    <option value={5}>5 на странице</option>
                    <option value={10}>10 на странице</option>
                    <option value={25}>25 на странице</option>
                    <option value={50}>50 на странице</option>
                </Form.Select>
            </div>

            <HrnbCard>
                <div className="admin-filters-row">
                <div className="admin-search-bar">
                    <Form.Control
                        type="text"
                        placeholder="Поиск постов..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <Dropdown>
                    <Dropdown.Toggle variant="outline-secondary" id="status-filter">
                        Статус: {statusFilter === 'ALL' ? 'Все' : getStatusLabel(statusFilter)}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setStatusFilter('ALL')}>Все</Dropdown.Item>
                        <Dropdown.Item onClick={() => setStatusFilter('PUBLISHED')}>Опубликованы</Dropdown.Item>
                        <Dropdown.Item onClick={() => setStatusFilter('HIDDEN')}>Скрыты</Dropdown.Item>
                        <Dropdown.Item onClick={() => setStatusFilter('DELETED')}>Удалены</Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <HrnbCard>
                <Table responsive hover className="admin-posts-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Заголовок</th>
                            <th>Автор</th>
                            <th>Категория</th>
                            <th>Статус</th>
                            <th>Просмотры</th>
                            <th>Лайки</th>
                            <th>Комментарии</th>
                            <th>Дата</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPosts.length === 0 ? (
                            <tr>
                                <td colSpan={10} className="text-center text-muted">
                                    Посты не найдены
                                </td>
                            </tr>
                        ) : (
                            filteredPosts.map((post) => (
                                <tr key={post.id}>
                                    <td>{post.id}</td>
                                    <td>
                                        <strong>{post.title}</strong>
                                    </td>
                                    <td>{post.author?.username || post.authorId}</td>
                                    <td>{post.category?.name || '-'}</td>
                                    <td>
                                        <Badge bg={getStatusBadgeVariant(post.status)}>
                                            {post.status}
                                        </Badge>
                                    </td>
                                    <td>{post.viewCount || 0}</td>
                                    <td>{post.likeCount || 0}</td>
                                    <td>{post.commentCount || 0}</td>
                                    <td>
                                        {post.createdAt 
                                            ? new Date(post.createdAt).toLocaleDateString('ru-RU')
                                            : '-'}
                                    </td>
                                    <td>
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            className="me-1"
                                            onClick={() => handleViewDetails(post)}
                                        >
                                            👁️
                                        </Button>
                                        
                                        {post.status === 'HIDDEN' && (
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="me-1"
                                                onClick={() => handleApprove(post)}
                                                disabled={actionLoading}
                                            >
                                                ✓ Опубликовать
                                            </Button>
                                        )}

                                        {post.status === 'PUBLISHED' && (
                                            <Button
                                                variant="outline-warning"
                                                size="sm"
                                                className="me-1"
                                                onClick={() => handleHide(post)}
                                                disabled={actionLoading}
                                            >
                                                👁️ Скрыть
                                            </Button>
                                        )}
                                        
                                        {post.status !== 'DELETED' && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDeletePost(post)}
                                                disabled={actionLoading}
                                            >
                                                🗑️ Удалить
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                <AdminPagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                />
            </HrnbCard>

            {/* Модальное окно с деталями поста */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Детали поста</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedPost && (
                        <>
                            <h5>{selectedPost.title}</h5>
                            <div className="text-muted small mb-3">
                                Автор: {selectedPost.author?.username || selectedPost.authorId} |
                                Дата: {new Date(selectedPost.createdAt).toLocaleString('ru-RU')} |
                                Статус: <Badge bg={getStatusBadgeVariant(selectedPost.status)}>{selectedPost.status}</Badge>
                            </div>
                            <div className="post-content">
                                {selectedPost.content}
                            </div>
                            <hr />
                            <div className="post-stats">
                                <strong>Статистика:</strong>
                                <ul className="list-unstyled mt-2">
                                    <li>Просмотры: {selectedPost.viewCount || 0}</li>
                                    <li>Лайки: {selectedPost.likeCount || 0}</li>
                                    <li>Комментарии: {selectedPost.commentCount || 0}</li>
                                </ul>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                        Закрыть
                    </Button>
                </Modal.Footer>
            </Modal>
            </HrnbCard>
        </Container>

    );
};

export default AdminForumPosts;
